import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import {
  type FiveSegmentDisplayColumnKey,
  type FiveSegmentSortColumnKey,
} from '~/apps/ninesegsAnalyzer/utils/customerAnalysis'
import {
  AnalysisCommentBlock,
  useGenerateSingleComment,
} from '~/components/features/AnalysisCommentBlock'
import { DisplayToggleButton } from '~/components/features/DisplayToggleButton'
import { ShareDropDownMenuButton } from '~/components/features/ShareDropDownMenuButton'
import {
  SortButton,
  type SortButtonProps,
} from '~/components/features/SortButton'
import { Table } from '~/components/features/Table'
import { TableContainer } from '~/components/features/TableContainer'
import { AmplitudeProvider } from '~/components/providers/AmplitudeEventProvider'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useSurveyQueryActiveBrandGroup } from '~/hooks/useSurveyQuery'
import {
  type DisplayColumnItem,
  exists,
  fiveSegmentIndicatorLabelI18nKeyMap,
  fiveSegmentIndicators,
  fiveSegmentLabelI18nKeyMap,
  fiveSegments,
  serializeTableToText,
  wrapFiveSegmentIndicatorLabelWithTranslate,
  wrapFiveSegmentLabelMapWithTranslate,
} from '~/utils'

import {
  fiveSegmentCommentAtom,
  fiveSegmentHiddenColumnKeysAtom,
  fiveSegmentSortValueAtom,
  hiddenRowIdsAtom,
  savedFiveSegmentCommentAtom,
} from '../atoms'
import { useRestoringParams } from '../hooks/restoringParams/useRestoringParams'
import { useDisplayRowItem } from '../hooks/useDisplayRowItem'
import { useFigureInfo } from '../hooks/useFigureInfo'
import { useFiveSegmentsTableColumns } from '../hooks/useFiveSegmentsTableColumns'
import { useFiveSegmentsTableInfo } from '../hooks/useFiveSegmentsTableInfo'
import { useSurveyId } from '../hooks/useSurveyId'

import styles from './FiveSegmentsTable.css'

// useLogEvent()はAmplitudeProviderの子孫でないと withSuffix が効かないため、
// Providerをレンダーする側とuseLogEvent()を呼ぶ側を分離する
export const FiveSegmentsTable = () => (
  <AmplitudeProvider analysisName="distribution" eventNameSuffix="_5segs">
    <FiveSegmentsTableInner />
  </AmplitudeProvider>
)

const FiveSegmentsTableInner = () => {
  const [comment, setComment] = useAtom(fiveSegmentCommentAtom)
  const savedComment = useAtomValue(savedFiveSegmentCommentAtom)
  const setSavedComment = useSetAtom(savedFiveSegmentCommentAtom)
  const { generate, isGenerating } = useGenerateSingleComment()
  const surveyId = useSurveyId()
  const brandGroup = useSurveyQueryActiveBrandGroup()
  const { t } = useTranslation()
  const fiveSegmentLabelMap = React.useMemo(
    () => wrapFiveSegmentLabelMapWithTranslate(fiveSegmentLabelI18nKeyMap, t),
    [t],
  )
  const fiveSegmentIndicatorLabelMap = React.useMemo(
    () =>
      wrapFiveSegmentIndicatorLabelWithTranslate(
        fiveSegmentIndicatorLabelI18nKeyMap,
        t,
      ),
    [t],
  )

  const sortButtonItems: SortButtonProps<FiveSegmentSortColumnKey>['items'] = [
    ...R.reverse(fiveSegments).map((fiveSegment) => ({
      columnKey: fiveSegment,
      label: t('5segs_distribution_fivesegmentlabelmapfivesegment', {
        fiveSegmentLabel: fiveSegmentLabelMap[fiveSegment],
      }),
    })),
    ...fiveSegmentIndicators.map((indicator) => ({
      columnKey: indicator,
      label: t('core_metrix_fivesegmentcore_metrixlabelmapcore_metrix', {
        fiveSegmentIndicatorLabel: fiveSegmentIndicatorLabelMap[indicator],
      }),
    })),
  ]
  const columns = useFiveSegmentsTableColumns()
  const tableInfo = useFiveSegmentsTableInfo({
    indicatorPad: styles.cellPadColumn,
  })

  const figureInfo = useFigureInfo('5segs')
  const restoringParams = useRestoringParams('5segs')
  const image = <Table columns={columns} tableInfo={tableInfo} />

  const [sortValue, setSortValue] = useAtom(fiveSegmentSortValueAtom)

  const displayRowItems = useDisplayRowItem('5segs')
  const setHiddenRowIds = useSetAtom(hiddenRowIdsAtom)

  const [hiddenColumnKeys, setHiddenColumnKeys] = useAtom(
    fiveSegmentHiddenColumnKeysAtom,
  )
  const displayColumnItems = React.useMemo<
    DisplayColumnItem<FiveSegmentDisplayColumnKey>[]
  >(
    () => [
      ...R.reverse(fiveSegments).map((fiveSegment) => ({
        columnKey: fiveSegment,
        isVisible: !hiddenColumnKeys.includes(fiveSegment),
        label: t('5segs_distribution_fivesegmentlabelmapfivesegment_1', {
          fiveSegmentLabel: fiveSegmentLabelMap[fiveSegment],
        }),
      })),
      ...fiveSegmentIndicators.map((indicator) => ({
        columnKey: indicator,
        isVisible: !hiddenColumnKeys.includes(indicator),
        label: t('core_metrix_fivesegmentcore_metrixlabelmapcore_metrix_1', {
          fiveSegmentIndicatorLabel: fiveSegmentIndicatorLabelMap[indicator],
        }),
      })),
    ],
    [fiveSegmentIndicatorLabelMap, fiveSegmentLabelMap, hiddenColumnKeys, t],
  )

  const logEvent = useLogEvent()
  const handleAutoGenerate = React.useCallback(async () => {
    const result = await generate({
      brandName: brandGroup?.name ?? '',
      // TODO: filterDescription は暫定で空文字（対象条件ブロックは出力されない）。実フィルター条件を渡す
      filterDescription: '',
      filterPatternIndex: 0,
      outputType: 'customerAnalysis',
      segmentMode: 'fiveSegs',
      surveyId,
      tabulationData: serializeTableToText(columns, tableInfo),
    })
    if (exists(result)) {
      setComment(result)
      logEvent('Generate AI comment', { withSuffix: true })
    }
  }, [
    generate,
    logEvent,
    brandGroup?.name,
    surveyId,
    columns,
    tableInfo,
    setComment,
  ])

  return (
    <>
      <AnalysisCommentBlock
        isGenerating={isGenerating}
        savedComment={savedComment}
        saveDocumentImage={{
          commentReadOnly: true,
          figureInfo,
          image,
          initialComment: comment,
          onSaved: (saved) => setSavedComment(saved),
          restoringParams,
        }}
        value={comment}
        onAutoGenerate={handleAutoGenerate}
        onCommentChange={setComment}
      />
      <TableContainer
        header={
          <>
            <SortButton
              items={sortButtonItems}
              value={sortValue}
              onChange={setSortValue}
            />

            <DisplayToggleButton
              columnItems={displayColumnItems}
              rowItems={displayRowItems}
              onHiddenColumnChange={setHiddenColumnKeys}
              onHiddenRowChange={setHiddenRowIds}
            />

            <ShareDropDownMenuButton
              copyToClipboard={{
                columns,
                figureInfo,
                restoringParams,
                tableInfo,
              }}
              downloadAsPng={{ image }}
              getSharedUrl={{ restoringParams }}
              saveDocumentImage={{
                commentReadOnly: true,
                figureInfo,
                image,
                initialComment: comment,
                onSaved: (saved) => setSavedComment(saved),
                restoringParams,
              }}
            />
          </>
        }
        id="5segs"
        title="5segs"
      >
        <Table columns={columns} stickyColumns={2} tableInfo={tableInfo} />
      </TableContainer>
    </>
  )
}
